import { useEffect, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useAuth } from '@/context/AuthContext';

const API_BASE = 'https://guardiancloudcare.com/backend/api';

interface NotificationRule {
  id: string;
  title: string;
  message: string;
  trigger: string;
  type: string;
  active: boolean;
}

interface EscalationConfig {
  id: string;
  name: string;
  condition: string;
  daysThreshold: number;
  notifyRole: string;
  active: boolean;
}

export default function AdminNotificationsPage() {
  const [notifications, setNotifications] = useState<NotificationRule[]>([]);
  const [escalations, setEscalations] = useState<EscalationConfig[]>([]);
  const [loading, setLoading] = useState(true);

  const [notifSaved, setNotifSaved] = useState(false);
  const { user } = useAuth();

  const [notifTab, setNotifTab] =
    useState<'rules' | 'escalation'>('rules');

  useEffect(() => {
    const loadNotificationConfig = async () => {
      try {
        setLoading(true);

        const response = await fetch(
          `${API_BASE}/get_notification_config.php`
        );

        if (!response.ok) {
          throw new Error(
            `HTTP error ${response.status}`
          );
        }

        const result = await response.json();

        if (!result.success || !Array.isArray(result.data)) {
          throw new Error(
            result.error ||
            'Invalid notification configuration response'
          );
        }

        result.data.forEach((config: any) => {
          if (
            config.config_key === 'notification_rules' &&
            Array.isArray(config.config_value)
          ) {
            setNotifications(config.config_value);
          }

          if (
            config.config_key === 'escalation_rules' &&
            Array.isArray(config.config_value)
          ) {
            setEscalations(config.config_value);
          }
        });

      } catch (error) {
        console.error(
          'Failed to load notification configuration:',
          error
        );

        alert(
          'Failed to load notification configuration.'
        );
      } finally {
        setLoading(false);
      }
    };

    loadNotificationConfig();
  }, []);

  const toggleNotification = (id: string) => {
    setNotifications((prev) =>
      prev.map((n) =>
        n.id === id
          ? { ...n, active: !n.active }
          : n
      )
    );
  };

  const toggleEscalation = (id: string) => {
    setEscalations((prev) =>
      prev.map((e) =>
        e.id === id
          ? { ...e, active: !e.active }
          : e
      )
    );
  };

  const createAuditLog = async (
    action: string,
    description: string
  ) => {
    try {
      if (!user) return;

      await fetch(
        `${API_BASE}/create_audit_log.php`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            uid: user.uid,
            user_name: user.name,
            user_role: user.role,
            action,
            module: 'Notifications',
            description,
          }),
        }
      );
    } catch (error) {
      console.error(
        'Failed to create notification audit log:',
        error
      );
    }
  };

  const handleSave = async () => {
    try {
      setNotifSaved(false);

      const notificationResponse = await fetch(
        `${API_BASE}/update_system_config.php`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            config_key: 'notification_rules',
            config_value: notifications,
          }),
        }
      );

      const notificationResult =
        await notificationResponse.json();

      if (
        !notificationResponse.ok ||
        !notificationResult.success
      ) {
        throw new Error(
          notificationResult.error ||
          'Failed to save notification rules'
        );
      }

      const escalationResponse = await fetch(
        `${API_BASE}/update_system_config.php`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            config_key: 'escalation_rules',
            config_value: escalations,
          }),
        }
      );

      const escalationResult =
        await escalationResponse.json();

      if (
        !escalationResponse.ok ||
        !escalationResult.success
      ) {
        throw new Error(
          escalationResult.error ||
          'Failed to save escalation rules'
        );
      }

      await createAuditLog(
        'Update',
        'Updated notification rules and escalation rules'
      );

      setNotifSaved(true);

      setTimeout(() => {
        setNotifSaved(false);
      }, 2500);

    } catch (error) {
      console.error(
        'Failed to save notification configuration:',
        error
      );

      alert(
        error instanceof Error
          ? error.message
          : 'Failed to save notification configuration.'
      );
    }
  };

  return (
    <AppLayout
      title="Notifications"
      breadcrumb={['Admin', 'Notifications']}
    >
      <div className="space-y-4">

        {loading && (
          <div className="bg-white rounded-xl border border-gray-100 p-6 text-center">
            <i className="ri-loader-4-line animate-spin text-teal-500 text-xl" />
            <p className="text-sm text-gray-500 mt-2">
              Loading notification configuration...
            </p>
          </div>
        )}

        <div className="flex gap-2 flex-wrap mb-4">
          {(['rules', 'escalation'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setNotifTab(t)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors cursor-pointer whitespace-nowrap capitalize ${
                notifTab === t
                  ? 'bg-teal-500 text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {t === 'rules'
                ? 'Notification Rules'
                : 'Escalation Rules'}
            </button>
          ))}
        </div>

        {!loading && notifTab === 'rules' && (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">

            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">
                Notification Rules
              </h3>

              <div className="flex items-center gap-2">
                {notifSaved && (
                  <span className="text-xs text-teal-600 bg-teal-50 border border-teal-200 px-3 py-1.5 rounded-lg flex items-center gap-1">
                    <i className="ri-checkbox-circle-line" />
                    Saved!
                  </span>
                )}

                <button
                  onClick={handleSave}
                  className="text-sm bg-teal-500 text-white px-4 py-2 rounded-xl hover:bg-teal-600 cursor-pointer whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-save-line" />
                  Save Rules
                </button>
              </div>
            </div>

            <div className="divide-y divide-gray-50">
              {notifications.map((n) => (
                <div
                  key={n.id}
                  className="px-5 py-4 flex items-start gap-4"
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      n.type === 'email'
                        ? 'bg-teal-50'
                        : n.type === 'sms'
                        ? 'bg-green-50'
                        : 'bg-gray-50'
                    }`}
                  >
                    <i
                      className={`text-sm ${
                        n.type === 'email'
                          ? 'ri-mail-line text-teal-500'
                          : n.type === 'sms'
                          ? 'ri-message-2-line text-green-500'
                          : 'ri-computer-line text-gray-500'
                      }`}
                    />
                  </div>

                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-800">
                      {n.title}
                    </p>

                    <p className="text-xs text-gray-500">
                      {n.message}
                    </p>

                    <p className="text-xs text-gray-400 mt-0.5">
                      Trigger: {n.trigger} · Type:{' '}
                      {n.type.toUpperCase()}
                    </p>
                  </div>

                  <button
                    onClick={() => toggleNotification(n.id)}
                    className={`w-12 h-6 rounded-full transition-all cursor-pointer relative flex-shrink-0 ${
                      n.active
                        ? 'bg-teal-500'
                        : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${
                        n.active
                          ? 'left-6'
                          : 'left-0.5'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && notifTab === 'escalation' && (
          <div className="space-y-3">

            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
              <i className="ri-alarm-warning-line text-red-500 mt-0.5" />

              <p className="text-sm text-red-700">
                Escalation rules automatically notify supervisors
                when social workers fail to act within the
                configured time threshold.
              </p>
            </div>

            {escalations.map((rule) => (
              <div
                key={rule.id}
                className="bg-white rounded-xl border border-gray-100 p-5"
              >
                <div className="flex items-start gap-4">

                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      rule.active
                        ? 'bg-red-50'
                        : 'bg-gray-50'
                    }`}
                  >
                    <i
                      className={`ri-alarm-warning-line text-lg ${
                        rule.active
                          ? 'text-red-500'
                          : 'text-gray-400'
                      }`}
                    />
                  </div>

                  <div className="flex-1">

                    <p className="text-sm font-semibold text-gray-800">
                      {rule.name}
                    </p>

                    <p className="text-xs text-gray-500 mb-1">
                      {rule.condition}
                    </p>

                    <div className="flex items-center gap-3 text-xs text-gray-400">

                      <span>
                        <i className="ri-time-line mr-1" />
                        Threshold:{' '}
                        <strong className="text-gray-700">
                          {rule.daysThreshold} day
                          {rule.daysThreshold > 1 ? 's' : ''}
                        </strong>
                      </span>

                      <span>
                        <i className="ri-user-line mr-1" />
                        Notify:{' '}
                        <strong className="text-gray-700">
                          {rule.notifyRole}
                        </strong>
                      </span>

                    </div>
                  </div>

                  <button
                    onClick={() =>
                      toggleEscalation(rule.id)
                    }
                    className={`w-12 h-6 rounded-full transition-all cursor-pointer relative flex-shrink-0 ${
                      rule.active
                        ? 'bg-teal-500'
                        : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${
                        rule.active
                          ? 'left-6'
                          : 'left-0.5'
                      }`}
                    />
                  </button>

                </div>
              </div>
            ))}

            <div className="flex gap-3 mt-2">

              <button
                onClick={handleSave}
                className="bg-teal-500 text-white text-sm px-5 py-2.5 rounded-xl hover:bg-teal-600 cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-save-line" />
                Save Rules
              </button>

              {notifSaved && (
                <span className="text-sm text-teal-600 bg-teal-50 border border-teal-200 px-3 py-2 rounded-xl flex items-center gap-1">
                  <i className="ri-checkbox-circle-line" />
                  Saved!
                </span>
              )}

            </div>
          </div>
        )}

      </div>
    </AppLayout>
  );
}